import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { cajasAPI, cierresCajaAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { imprimirCierreCaja } from '../utils/exportListado';
import { useBranding } from '../context/BrandingContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import '../Clientes.css';

const formatMoney = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CierreCaja = () => {
  const { hasPermission } = useAuth();
  const { logoUrl } = useBranding();
  const { fechaHoy, formatFecha } = useZonaHoraria();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cajas, setCajas] = useState([]);
  const [cajaId, setCajaId] = useState(searchParams.get('caja_id') || '');
  const [fecha, setFecha] = useState('');
  const [preparado, setPreparado] = useState(null);
  const [denominaciones, setDenominaciones] = useState([]);
  const [chequesMarcados, setChequesMarcados] = useState(new Set());
  const [observaciones, setObservaciones] = useState('');
  const [crearAjuste, setCrearAjuste] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    setFecha((prev) => prev || fechaHoy());
  }, [fechaHoy]);

  useEffect(() => {
    const loadCajas = async () => {
      try {
        const res = await cajasAPI.getAll({ activo: true });
        if (!res.success) {
          toast.error(res.message || 'Error al cargar cajas');
          return;
        }
        let list = res.data || [];
        const preId = searchParams.get('caja_id');
        if (preId && !list.some((c) => String(c.id) === String(preId))) {
          try {
            const one = await cajasAPI.getById(preId);
            if (one.success && one.data) {
              list = [one.data, ...list];
            }
          } catch {
            /* caja preseleccionada inactiva o inexistente */
          }
        }
        setCajas(list);
        if (list.length === 0) {
          toast.warning('No hay cajas activas. Revise el ABM de Cajas o active una caja.');
        }
      } catch (e) {
        toast.error(e.message || 'Error al cargar cajas para el cierre');
      }
    };
    loadCajas();
  }, [searchParams]);

  const cargarPreparacion = useCallback(async () => {
    if (!cajaId || !fecha) {
      toast.warning('Seleccione caja y fecha');
      return;
    }
    try {
      setLoading(true);
      setResultado(null);
      const res = await cierresCajaAPI.preparar({ caja_id: cajaId, fecha });
      if (!res.success) throw new Error(res.message);
      setPreparado(res.data);
      setDenominaciones(
        (res.data.denominaciones || []).map((d) => ({ ...d, cantidad: d.cantidad || 0 }))
      );
      const idsEsperados = new Set((res.data.cheques_esperados || []).map((c) => c.id));
      setChequesMarcados(idsEsperados);
    } catch (e) {
      toast.error(e.message || 'Error al preparar cierre');
      setPreparado(null);
    } finally {
      setLoading(false);
    }
  }, [cajaId, fecha]);

  const totalFisicoEfectivo = useMemo(() => {
    return denominaciones.reduce((s, d) => {
      const cant = parseFloat(d.cantidad) || 0;
      if (d.es_monto_libre) return s + cant;
      return s + cant * (parseFloat(d.valor_unitario) || 0);
    }, 0);
  }, [denominaciones]);

  const chequesEsperados = preparado?.cheques_esperados || [];
  const totalFisicoCheques = useMemo(() => {
    return chequesEsperados
      .filter((c) => chequesMarcados.has(c.id))
      .reduce((s, c) => s + (parseFloat(c.importe) || 0), 0);
  }, [chequesEsperados, chequesMarcados]);

  const diferencias = useMemo(() => {
    if (!preparado) return null;
    const difEf = totalFisicoEfectivo - (parseFloat(preparado.saldo_sistema_efectivo) || 0);
    const difCh = totalFisicoCheques - (parseFloat(preparado.saldo_sistema_cheques) || 0);
    return {
      efectivo: difEf,
      cheques: difCh,
      total: difEf + difCh,
    };
  }, [preparado, totalFisicoEfectivo, totalFisicoCheques]);

  const toggleCheque = (id) => {
    setChequesMarcados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const actualizarCantidad = (denId, valor) => {
    setDenominaciones((prev) =>
      prev.map((d) => (d.id === denId ? { ...d, cantidad: valor } : d))
    );
  };

  const confirmarCierre = async () => {
    if (!preparado) return;
    try {
      setGuardando(true);
      const res = await cierresCajaAPI.crear({
        caja_id: cajaId,
        fecha,
        observaciones,
        crear_ajuste: crearAjuste,
        denominaciones: denominaciones.map((d) => ({
          denominacion_id: d.id,
          cantidad: parseFloat(d.cantidad) || 0,
        })),
        cheques_marcados: [...chequesMarcados],
      });
      if (!res.success) throw new Error(res.message);
      toast.success(res.message);
      setResultado(res.data);
    } catch (e) {
      toast.error(e.message || 'Error al guardar cierre');
    } finally {
      setGuardando(false);
    }
  };

  const imprimirCierre = () => {
    const base = resultado || preparado;
    if (!base) return;
    try {
      const filasEf = (resultado?.efectivo || denominaciones)
        .filter((d) => (parseFloat(d.cantidad) || 0) > 0 || (parseFloat(d.subtotal) || 0) > 0)
        .map((d) => ({
          nombre: d.nombre,
          cantidad: d.cantidad,
          subtotal: d.subtotal != null
            ? parseFloat(d.subtotal) || 0
            : d.es_monto_libre
              ? parseFloat(d.cantidad) || 0
              : (parseFloat(d.cantidad) || 0) * (parseFloat(d.valor_unitario) || 0),
        }));

      const chequesLista = resultado
        ? (resultado.cheques || []).filter((ch) => !!ch.marcado_fisico)
        : (preparado?.cheques_esperados || []).filter((ch) => chequesMarcados.has(ch.id));

      const resumen = resultado
        ? [
            { label: 'Saldo sistema total', value: formatMoney(resultado.saldo_sistema_total) },
            { label: 'Efectivo sistema', value: formatMoney(resultado.saldo_sistema_efectivo) },
            { label: 'Cheques sistema', value: formatMoney(resultado.saldo_sistema_cheques) },
            { label: 'Físico efectivo', value: formatMoney(resultado.total_fisico_efectivo) },
            { label: 'Físico cheques', value: formatMoney(resultado.total_fisico_cheques) },
          ]
        : preparado
          ? [
              { label: 'Saldo sistema total', value: formatMoney(preparado.saldo_sistema_total) },
              { label: 'Efectivo sistema', value: formatMoney(preparado.saldo_sistema_efectivo) },
              { label: 'Cheques sistema', value: formatMoney(preparado.saldo_sistema_cheques) },
              { label: 'Físico efectivo', value: formatMoney(totalFisicoEfectivo) },
              { label: 'Físico cheques', value: formatMoney(totalFisicoCheques) },
            ]
          : [];

      const dif = resultado
        ? {
            efectivo: resultado.diferencia_efectivo,
            cheques: resultado.diferencia_cheques,
            total: resultado.diferencia_total,
          }
        : diferencias
          ? { efectivo: diferencias.efectivo, cheques: diferencias.cheques, total: diferencias.total }
          : null;

      imprimirCierreCaja({
        titulo: resultado
          ? `Cierre de caja N° ${resultado.numero} — ${resultado.caja_nombre || preparado?.caja?.nombre}`
          : `Borrador cierre — ${preparado?.caja?.nombre}`,
        subtitulo: `Fecha: ${fecha}`,
        resumen,
        efectivo: filasEf,
        cheques: chequesLista.map((ch) => ({
          numero: ch.numero,
          cliente_razon_social: ch.cliente_razon_social,
          importe: parseFloat(ch.importe) || 0,
          marcado: true,
        })),
        diferencias: dif,
        ajuste: resultado?.movimiento_ajuste
          ? {
              id: resultado.movimiento_ajuste.id,
              tipo: resultado.movimiento_ajuste.tipo,
              monto: resultado.movimiento_ajuste.monto,
              concepto: resultado.movimiento_ajuste.concepto,
            }
          : null,
        observaciones: resultado?.observaciones || observaciones,
        logoUrl,
      });
    } catch (e) {
      toast.error(e.message || 'Error al imprimir');
    }
  };

  if (!hasPermission('cerrar_caja')) {
    return (
      <div className="clientes">
        <p>No tiene permiso para cerrar caja.</p>
      </div>
    );
  }

  return (
    <div className="clientes">
      <div className="page-header">
        <h1>Cierre de caja</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/cierres-caja" className="btn-secondary">Historial</Link>
          <Link to="/fondos/transferencia-cheques" className="btn-secondary">Transferir cheques</Link>
        </div>
      </div>

      <div className="filters-section" style={{ marginBottom: 16 }}>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Caja *</label>
            <select value={cajaId} onChange={(e) => { setCajaId(e.target.value); setPreparado(null); }}>
              <option value="">Seleccione...</option>
              {cajas.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}{c.activa === 0 || c.activa === false ? ' (inactiva)' : ''}</option>
              ))}
            </select>
            {cajas.length === 0 && (
              <p style={{ marginTop: 8, fontSize: '0.9rem' }}>
                <Link to="/cajas">Ir a Cajas</Link> para activar o crear una caja.
              </p>
            )}
          </div>
          <div className="filter-group">
            <label>Fecha de cierre *</label>
            <input type="date" value={fecha} onChange={(e) => { setFecha(e.target.value); setPreparado(null); }} />
          </div>
          <div className="filter-group" style={{ alignSelf: 'end' }}>
            <button type="button" className="btn-primary" onClick={cargarPreparacion} disabled={loading}>
              {loading ? 'Cargando…' : 'Cargar datos'}
            </button>
          </div>
        </div>
      </div>

      {preparado && !resultado && (
        <>
          <section className="table-container" style={{ marginBottom: 16, padding: 16 }}>
            <h2 style={{ marginTop: 0 }}>Resumen sistema — {preparado.caja?.nombre}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div><strong>Saldo inicial día:</strong><br />{formatMoney(preparado.saldo_inicial_dia)}</div>
              <div><strong>Ingresos del día:</strong><br />{formatMoney(preparado.total_ingresos_dia)}</div>
              <div><strong>Egresos del día:</strong><br />{formatMoney(preparado.total_egresos_dia)}</div>
              <div><strong>Saldo sistema total:</strong><br />{formatMoney(preparado.saldo_sistema_total)}</div>
              <div><strong>Efectivo sistema:</strong><br />{formatMoney(preparado.saldo_sistema_efectivo)}</div>
              <div><strong>Cheques sistema:</strong><br />{formatMoney(preparado.saldo_sistema_cheques)}</div>
            </div>
          </section>

          <section className="table-container" style={{ marginBottom: 16 }}>
            <h2>Arqueo de efectivo</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Denominación</th>
                  <th>Valor unitario</th>
                  <th>{denominaciones.some((d) => d.es_monto_libre) ? 'Monto / Cantidad' : 'Cantidad'}</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {denominaciones.map((d) => {
                  const cant = parseFloat(d.cantidad) || 0;
                  const sub = d.es_monto_libre ? cant : cant * (parseFloat(d.valor_unitario) || 0);
                  return (
                    <tr key={d.id}>
                      <td>{d.nombre}</td>
                      <td>{d.es_monto_libre ? '—' : formatMoney(d.valor_unitario)}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step={d.es_monto_libre ? '0.01' : '1'}
                          value={d.cantidad}
                          onChange={(e) => actualizarCantidad(d.id, e.target.value)}
                          style={{ width: 120 }}
                        />
                      </td>
                      <td>{formatMoney(sub)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>Total físico efectivo</td>
                  <td style={{ fontWeight: 600 }}>{formatMoney(totalFisicoEfectivo)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="table-container" style={{ marginBottom: 16 }}>
            <h2>Cheques en cartera (marcar los físicamente presentes)</h2>
            {chequesEsperados.length === 0 ? (
              <p className="no-data">No hay cheques esperados en esta caja.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>N° cheque</th>
                    <th>Banco</th>
                    <th>Cliente</th>
                    <th>Importe</th>
                    <th>F. cobro</th>
                  </tr>
                </thead>
                <tbody>
                  {chequesEsperados.map((ch) => (
                    <tr key={ch.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={chequesMarcados.has(ch.id)}
                          onChange={() => toggleCheque(ch.id)}
                        />
                      </td>
                      <td>{ch.numero}</td>
                      <td>{ch.banco_nombre || '—'}</td>
                      <td>{ch.cliente_razon_social}</td>
                      <td>{formatMoney(ch.importe)}</td>
                      <td>{ch.fecha_cobro ? formatFecha(ch.fecha_cobro) : '—'}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right', fontWeight: 600 }}>Total cheques marcados</td>
                    <td colSpan={2} style={{ fontWeight: 600 }}>{formatMoney(totalFisicoCheques)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </section>

          {diferencias && (
            <section className="table-container" style={{ marginBottom: 16, padding: 16 }}>
              <h2 style={{ marginTop: 0 }}>Diferencias</h2>
              <p>Efectivo: <strong style={{ color: Math.abs(diferencias.efectivo) < 0.01 ? 'green' : 'crimson' }}>{formatMoney(diferencias.efectivo)}</strong></p>
              <p>Cheques: <strong style={{ color: Math.abs(diferencias.cheques) < 0.01 ? 'green' : 'crimson' }}>{formatMoney(diferencias.cheques)}</strong></p>
              <p>Total: <strong style={{ color: Math.abs(diferencias.total) < 0.01 ? 'green' : 'crimson' }}>{formatMoney(diferencias.total)}</strong></p>
              <div className="filter-group" style={{ marginTop: 12 }}>
                <label>Observaciones</label>
                <textarea
                  rows={3}
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  style={{ width: '100%', maxWidth: 600 }}
                />
              </div>
              {Math.abs(diferencias.total) >= 0.01 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={crearAjuste}
                    onChange={(e) => setCrearAjuste(e.target.checked)}
                  />
                  Crear movimiento de ajuste por la diferencia total ({formatMoney(diferencias.total)})
                </label>
              )}
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button type="button" className="btn-primary" onClick={confirmarCierre} disabled={guardando}>
                  {guardando ? 'Guardando…' : 'Confirmar cierre'}
                </button>
                <button type="button" className="btn-secondary" onClick={imprimirCierre}>Vista previa impresión</button>
              </div>
            </section>
          )}
        </>
      )}

      {resultado && (
        <section className="table-container" style={{ padding: 16 }}>
          <h2 style={{ marginTop: 0, color: 'green' }}>Cierre registrado N° {resultado.numero}</h2>
          <p>Caja: <strong>{resultado.caja_nombre}</strong> — Fecha: {resultado.fecha_cierre}</p>
          <p>Diferencia total: {formatMoney(resultado.diferencia_total)}</p>
          {resultado.movimiento_ajuste && (
            <p>
              Ajuste registrado: movimiento #{resultado.movimiento_ajuste.id} —{' '}
              {resultado.movimiento_ajuste.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} de{' '}
              {formatMoney(resultado.movimiento_ajuste.monto)}
            </p>
          )}
          {!resultado.movimiento_ajuste && resultado.movimiento_ajuste_id && (
            <p>Se creó movimiento de ajuste #{resultado.movimiento_ajuste_id}</p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn-primary" onClick={imprimirCierre}>Imprimir</button>
            <button type="button" className="btn-secondary" onClick={() => navigate(`/cierres-caja/${resultado.id}`)}>
              Ver detalle
            </button>
            <button type="button" className="btn-secondary" onClick={() => { setResultado(null); cargarPreparacion(); }}>
              Nuevo cierre
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default CierreCaja;
